import {
  useMutation,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { queryKeys, useAuthenticatedClient } from "@/api/queries";
import {
  applyMutationToShell,
  errorMessage,
  mergeFields,
  mergeIssueDelta,
  refreshOperationalViews,
} from "./cache";
import {
  insertLineInPages,
  optimisticallyPatchLineInPages,
  removeLinesFromPages,
  replaceLineInPages,
  restoreLinesInPages,
} from "./projections";
import type {
  DropshipV2Field,
  DropshipV2Issue,
  DropshipV2Line,
  DropshipV2LineChanges,
  DropshipV2LineCreate,
  DropshipV2LinePage,
  DropshipV2Shell,
} from "./types";

export function usePatchDropshipV2Line(draftUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const lineKey = queryKeys.dropshipV2.lineGroups(draftUuid);
  const issueKey = queryKeys.dropshipV2.issues(draftUuid);
  const fieldKey = queryKeys.dropshipV2.fields(draftUuid);
  return useMutation({
    mutationFn: async ({
      line,
      changes,
    }: {
      line: DropshipV2Line;
      changes: DropshipV2LineChanges;
    }) => {
      const client = await getClient();
      const { data, error } = await client.PATCH(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/lines/{line_uuid}",
        {
          params: { path: { draft_uuid: draftUuid, line_uuid: line.uuid } },
          body: { expected_revision: line.revision, ...changes },
        },
      );
      if (error) throw error;
      return data;
    },
    onMutate: async ({ line, changes }) => {
      await queryClient.cancelQueries({ queryKey: lineKey });
      const previousLines =
        queryClient.getQueryData<InfiniteData<DropshipV2LinePage>>(lineKey);
      const optimistic = optimisticallyPatchLineInPages(
        previousLines,
        line,
        changes,
      );
      queryClient.setQueryData(lineKey, optimistic.data);
      return { previousLines: optimistic.previousLines };
    },
    onError: (error, _variables, context) => {
      if (context?.previousLines) {
        queryClient.setQueryData<InfiniteData<DropshipV2LinePage>>(
          lineKey,
          (current) => restoreLinesInPages(current, context.previousLines),
        );
      }
      toast.error("Line edit was not saved", {
        description: errorMessage(error, "The prior line value was restored."),
      });
    },
    onSuccess: async (result) => {
      if (result.line) {
        queryClient.setQueryData<InfiniteData<DropshipV2LinePage>>(
          lineKey,
          (current) =>
            replaceLineInPages(current, result.line as DropshipV2Line),
        );
      }
      for (const relatedLine of result.related_lines) {
        queryClient.setQueryData<InfiniteData<DropshipV2LinePage>>(
          lineKey,
          (current) => replaceLineInPages(current, relatedLine),
        );
      }
      queryClient.setQueryData<DropshipV2Field[]>(fieldKey, (current) =>
        mergeFields(current, result.related_fields),
      );
      await applyMutationToShell(queryClient, draftUuid, result);
      queryClient.setQueryData<DropshipV2Issue[]>(issueKey, (current) =>
        mergeIssueDelta(current, result.issue_delta),
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropshipV2.clarifications(draftUuid),
      });
      refreshOperationalViews(queryClient, draftUuid);
    },
  });
}

export function useCreateDropshipV2Line(draftUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const lineKey = queryKeys.dropshipV2.lineGroups(draftUuid);
  const fieldKey = queryKeys.dropshipV2.fields(draftUuid);
  const issueKey = queryKeys.dropshipV2.issues(draftUuid);
  return useMutation({
    mutationFn: async (body: DropshipV2LineCreate) => {
      const client = await getClient();
      const { data, error } = await client.POST(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/lines",
        { params: { path: { draft_uuid: draftUuid } }, body },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async (result) => {
      if (result.line) {
        queryClient.setQueryData<InfiniteData<DropshipV2LinePage>>(
          lineKey,
          (current) =>
            insertLineInPages(current, result.line as DropshipV2Line),
        );
      }
      queryClient.setQueryData<DropshipV2Field[]>(fieldKey, (current) =>
        mergeFields(current, result.related_fields),
      );
      queryClient.setQueryData<DropshipV2Issue[]>(issueKey, (current) =>
        mergeIssueDelta(current, result.issue_delta),
      );
      await applyMutationToShell(queryClient, draftUuid, result, (shell) => ({
        total_line_count: shell.total_line_count + 1,
        root_line_count:
          shell.root_line_count + Number(result.line?.depth === 0),
      }));
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropshipV2.clarifications(draftUuid),
      });
      refreshOperationalViews(queryClient, draftUuid);
    },
  });
}

/** @deprecated Use useCreateDropshipV2Line. */
export const useCreateDropshipV2ScratchLine = useCreateDropshipV2Line;

export function useRemoveDropshipV2Line(draftUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const lineKey = queryKeys.dropshipV2.lineGroups(draftUuid);
  const shellKey = queryKeys.dropshipV2.shell(draftUuid);
  const fieldKey = queryKeys.dropshipV2.fields(draftUuid);
  const issueKey = queryKeys.dropshipV2.issues(draftUuid);
  return useMutation({
    mutationFn: async ({ line }: { line: DropshipV2Line }) => {
      const client = await getClient();
      const { data, error } = await client.DELETE(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/lines/{line_uuid}",
        {
          params: {
            path: { draft_uuid: draftUuid, line_uuid: line.uuid },
            query: { expected_revision: line.revision },
          },
        },
      );
      if (error) throw error;
      return data;
    },
    onMutate: async ({ line }) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: lineKey }),
        queryClient.cancelQueries({ queryKey: shellKey }),
      ]);
      const previousLines =
        queryClient.getQueryData<InfiniteData<DropshipV2LinePage>>(lineKey);
      const previousShell = queryClient.getQueryData<DropshipV2Shell>(shellKey);
      const group = previousLines?.pages
        .flatMap((page) => page.items)
        .find(
          (item) =>
            item.root.uuid === line.uuid ||
            item.children.some((child) => child.uuid === line.uuid),
        );
      const optimisticRemoved = new Set(
        line.depth === 0 && group
          ? [group.root.uuid, ...group.children.map((child) => child.uuid)]
          : [line.uuid],
      );
      queryClient.setQueryData(
        lineKey,
        removeLinesFromPages(previousLines, optimisticRemoved),
      );
      queryClient.setQueryData<DropshipV2Shell>(shellKey, (shell) =>
        shell
          ? {
              ...shell,
              total_line_count: Math.max(
                0,
                shell.total_line_count - optimisticRemoved.size,
              ),
              root_line_count: Math.max(
                0,
                shell.root_line_count - Number(line.depth === 0),
              ),
            }
          : shell,
      );
      return { previousLines, previousShell };
    },
    onError: (error, _variables, context) => {
      if (context?.previousLines)
        queryClient.setQueryData(lineKey, context.previousLines);
      if (context?.previousShell)
        queryClient.setQueryData(shellKey, context.previousShell);
      toast.error("Line was not removed", {
        description: errorMessage(
          error,
          "The line and its prior totals were restored.",
        ),
      });
    },
    onSuccess: async (result) => {
      queryClient.setQueryData<InfiniteData<DropshipV2LinePage>>(
        lineKey,
        (current) =>
          removeLinesFromPages(current, new Set(result.removed_line_uuids)),
      );
      queryClient.setQueryData<DropshipV2Field[]>(fieldKey, (current) =>
        mergeFields(current, result.related_fields),
      );
      queryClient.setQueryData<DropshipV2Issue[]>(issueKey, (current) =>
        mergeIssueDelta(current, result.issue_delta),
      );
      await applyMutationToShell(queryClient, draftUuid, result);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropshipV2.clarifications(draftUuid),
      });
      refreshOperationalViews(queryClient, draftUuid);
      toast.success(
        `${result.removed_line_uuids.length} line${result.removed_line_uuids.length === 1 ? "" : "s"} removed`,
      );
    },
  });
}
